/**
 * WidgetInspector — "All settings" for the selected widget.
 *
 * Non-modal and docked beside the canvas (Duro DetailPanel), so the canvas
 * stays live while a field changes and a click on another widget switches the
 * panel to it; below the md breakpoint there is no room beside the canvas and
 * it becomes a bottom Drawer, as DetailPanel's own guidance says. It closes
 * when the selection clears, when the item goes away, or on Escape — which
 * then hands focus back to whatever opened it.
 *
 * Its content is the form the app registered for the kind
 * (registerWidgetSettings). Pending text is flushed whenever the surface
 * closes or moves to another item, so nothing typed is lost or written to the
 * wrong widget.
 */
import { useEffect, useRef } from "react";
import { DetailPanel, Drawer } from "@duro-app/ui";
import { useGridStore } from "@fredericrous/grid";
import {
  closeSettings,
  flushPendingEdits,
  openSettings,
  registeredWidgetSettings,
  useIsMobile,
  useSelectionView,
  useSettingsTarget,
  widgetKindName,
} from "@website-builder/editor";

export function WidgetInspector(): React.ReactElement | null {
  const target = useSettingsTarget();
  const view = useSelectionView();
  const isMobile = useIsMobile();
  const item = useGridStore((s) =>
    target ? s.grids[target.gridId]?.items[target.itemId] : undefined,
  );
  const opener = useRef<HTMLElement | null>(null);

  // Remember what had focus when the surface opened, to give it back.
  const wasOpen = useRef(false);
  useEffect(() => {
    const open = target !== null;
    if (open && !wasOpen.current)
      opener.current = document.activeElement as HTMLElement | null;
    if (!open && wasOpen.current) {
      opener.current?.focus?.();
      opener.current = null;
    }
    wasOpen.current = open;
  }, [target]);

  // Follow the selection: another widget with a form → switch to it;
  // anything else (nothing, a section, several blocks) → close.
  useEffect(() => {
    if (!target) return;
    if (
      view.kind === "widget" &&
      registeredWidgetSettings(view.contentType) !== undefined
    ) {
      if (view.gridId !== target.gridId || view.itemId !== target.itemId) {
        flushPendingEdits();
        openSettings(view.gridId, view.itemId);
      }
      return;
    }
    flushPendingEdits();
    closeSettings();
  }, [view, target]);

  // The item went away (deleted, undone): nothing left to edit.
  useEffect(() => {
    if (target && !item) closeSettings();
  }, [target, item]);

  if (!target || !item) return null;
  const Form = registeredWidgetSettings(item.contentType);
  if (!Form) return null;

  const title = widgetKindName(
    item.contentType,
    item.contentProps as Record<string, unknown> | undefined,
  );
  const close = (): void => {
    flushPendingEdits();
    closeSettings();
  };
  const body = (
    // eslint-disable-next-line duro/no-raw-html-element -- the settings-surface marker is a data attribute (the editor's key guard finds it with closest()), which react-strict-dom's html.* cannot forward
    <div
      data-wb-settings-surface=""
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          close();
        } else if (
          e.key === "Enter" &&
          !(e.target instanceof HTMLTextAreaElement)
        ) {
          flushPendingEdits();
        }
      }}
    >
      <Form gridId={target.gridId} itemId={target.itemId} />
    </div>
  );

  if (isMobile)
    return (
      <Drawer.Root
        open
        anchor="bottom"
        onOpenChange={(next) => {
          if (!next) close();
        }}
      >
        <Drawer.Portal size="md">
          <Drawer.Header>
            <Drawer.Title>{title}</Drawer.Title>
            <Drawer.Close />
          </Drawer.Header>
          <Drawer.Body>{body}</Drawer.Body>
        </Drawer.Portal>
      </Drawer.Root>
    );

  return (
    <DetailPanel.Root
      open
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DetailPanel.Content size="sm" label={`${title} settings`}>
        <DetailPanel.Header>
          <DetailPanel.Title>{title}</DetailPanel.Title>
          <DetailPanel.Close />
        </DetailPanel.Header>
        <DetailPanel.Body>{body}</DetailPanel.Body>
      </DetailPanel.Content>
    </DetailPanel.Root>
  );
}
